import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import errorSvg from 'src/assets/svg/error.svg';
import successSvg from 'src/assets/svg/success.svg';
import { appgamiAutomationAuthorizationUrl, createAzureAuthorizationUrl } from 'src/utils/azure-authorization-url.util';

export default function UserAuthorizationPage() {
	const classes = useStyles();
	const [pageType, setPageType] = useState<'did-not-authorize' | 'authorized' | null>(null);
	const [failureReason, setFailureReason] = useState<'access_denied' | null>(null);

	const cancel = useCallback(() => {
		window.sessionStorage.clear();
		window.close();
	}, []);

	const reloadAuthorization = useCallback(() => {
		window.location.href = appgamiAutomationAuthorizationUrl({
			organizationName: window.sessionStorage.getItem('organization_name') as string,
			organizationId: window.sessionStorage.getItem('organization_id') as string
		});
	}, []);

	useEffect(() => {
		document.title = 'Appgami Automation - Authorization';
	}, []);

	const init = useRef(true);
	useEffect(() => {
		if (init.current) {
			init.current = false;
			const url = new URLSearchParams(window.location.search);

			const result = url.get('result');
			const organizationName = url.get('organization_name') as string;
			const organizationId = url.get('organization_id') as string;

			window.sessionStorage.setItem('organization_name', organizationName);
			window.sessionStorage.setItem('organization_id', organizationId);

			if (result && result === 'did-not-authorize') {
				setPageType('did-not-authorize');
				const reason = url.get('reason');
				if (reason === 'access_denied') {
					setFailureReason('access_denied');
				}
			}

			if (result && result === 'authorized') {
				setPageType('authorized');
			}

			if (!result) {
				window.location.href = createAzureAuthorizationUrl({
					organizationName,
					organizationId
				});
			}
		}
	}, []);

	return (
		<div className={classes.root}>
			{pageType === 'did-not-authorize' && (
				<div className={classes.content}>
					<div className={classes.imageRoot}>
						<img
							className={classes.icon}
							alt="not-authorized"
							src={errorSvg}
						/>
					</div>
					<div className={classes.textRoot}>
						<p className={classes.text}>
							{failureReason === 'access_denied' ? (
								<>
									<strong>Admin approval required</strong>
									<br />
									<br />
									Your organization requires an administrator to approve Appgami Automation before you can connect your account.
									<br />
									<br />
									To resolve this:
									<br />
									1. Contact your Azure DevOps organization administrator.
									<br />
									2. Ask them to approve Appgami Automation for your organization. This should likely be done in Microsoft Entra.
									<br />
									3. Once they have approved, click <strong>Try again</strong> below.
								</>
							) : (
								<>
									We couldn&apos;t complete the authorization. Please try again.
									<br />
									<br />
									If the problem persists, check your permissions in Azure DevOps or contact your administrator.
								</>
							)}
						</p>
					</div>
					<div className={classes.bottom}>
						<Button
							appearance='outline'
							onClick={cancel}
						>
							Close
						</Button>
						<Button
							appearance='primary'
							onClick={reloadAuthorization}
						>
							Try again
						</Button>
					</div>
				</div>
			)}
			{pageType === 'authorized' && (
				<div className={classes.content}>
					<div className={classes.imageRoot}>
						<img
							className={classes.icon}
							alt="not-authorized"
							src={successSvg}
						/>
					</div>
					<div className={classes.textRoot}>
						<p className={classes.text}>
							Appgami Automation is now connected to Azure DevOps. <br />You can start creating and running automation rules right away.
						</p>
					</div>
					<div className={classes.bottom}>
						<div>
							<Button
								appearance='primary'
								onClick={cancel}
							>
								Let&apos;s go
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export const useStyles = makeStyles({
	root: {
		backgroundColor: tokens.colorNeutralForegroundInverted,
		height: '100%',
		width: '100%',
		position: 'absolute',
		left: 0,
		top: 0,
		boxSizing: 'border-box'
	},
	content: {
		width: '100%',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'column',
		boxSizing: 'border-box'
	},
	imageRoot: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '24px 0',
		boxSizing: 'border-box'
	},
	icon: {
		height: 'auto',
		width: '128px'
	},
	textRoot: {
		boxSizing: 'border-box',
		width: 'fit-content',
		padding: '12px',
	},
	text: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase400,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase400,
		textAlign: 'center',
		'& strong': {
			fontWeight: tokens.fontWeightBold,
			lineHeight: tokens.lineHeightBase300,
		}
	},
	bottom: {
		display: 'flex',
		gap: '8px',
		padding: '24px 0px',
		boxSizing: 'border-box'
	}
});